import ModalViewModel from 'infra/ModalViewModel'
import QuestionStore, { QuestionsQuerySession } from 'jobs/QuestionStore'
import Job from 'jobs/Job'
import QuestionListViewModel from 'jobs/questions/QuestionListViewModel'
import { map, startWith, switchMap, take, takeUntil } from 'rxjs/operators'
import { action, computed, observable, makeObservable } from 'mobx'
import { AnswerDTO, QuestionsResponseDTO } from 'jobs/dtos/questions'
import { task, TaskGroup } from 'mobx-task'
import JobStore from 'jobs/JobStore'
import { fromStream } from 'utils/mobx-util'
import { AlertFactory, AlertState } from 'alerts/Alert'

/**
 * Corrections view model.
 */
export default class CorrectionsViewModel extends ModalViewModel {
  /**
   * Questions.
   */
  questions: QuestionListViewModel

  /**
   * Questions query session.
   */
  session = this.createQuestionsQuerySession()

  /**
   * Task group for fetching and saving.
   */
  tasks: TaskGroup<any, any>

  /**
   * Whether all questions have been answered.
   */
  @observable done = false

  /**
   * Reason for the madness.
   */
  @observable reason = ''

  /**
   * Getter for whether the answers have changed.
   */
  @observable hasChanged = () => false

  constructor(
    public job: Job,
    private createQuestionsQuerySession: () => QuestionsQuerySession,
    private questionStore: QuestionStore,
    private jobStore: JobStore,
    private createQuestionListViewModel: () => QuestionListViewModel,
    private newAlert: AlertFactory,
  ) {
    super()
    makeObservable(this)
    this.tasks = TaskGroup<any, any>([this.fetchQuestions, this.save])
    this.questions = this.createQuestionListViewModel()
  }

  /**
   * Whether we can save responses.
   */
  @computed
  get canSaveResponses() {
    return this.done && this.hasChanged()
  }

  /**
   * Shows the dialog.
   */
  show() {
    if (this.showing) {
      return
    }
    this.fetchQuestions.reset()
    this.done = false
    this.reason = ''
    this.questions = this.createQuestionListViewModel()
    this.session = this.createQuestionsQuerySession()
    this.hasChanged = fromStream<boolean>(
      false,
      this.questions.answersChanged$.pipe(
        map(() => true),
        take(1),
        // Keep listening until we close the dialog.
        takeUntil(this.close$),
      ),
    )
    this.questions.answersChanged$
      .pipe(
        // Start with the job answers we have.
        startWith(this.job.answers.slice()),
        // Fetch questions
        switchMap(this.fetchQuestions),
        // Keep listening until we close the dialog.
        takeUntil(this.close$),
      )
      .subscribe(
        action((result: QuestionsResponseDTO) => {
          this.done = result.done
          this.questions.reconcile(result.questions)
        }),
      )

    super.show()
  }

  /**
   * Fetches questions.
   */
  fetchQuestions = task((answers: AnswerDTO[]) =>
    this.session.fetchQuestions(answers),
  )

  /**
   * Saves the responses.
   */
  save = task.resolved(async (): Promise<boolean> => {
    if (!this.questions.validate()) {
      return false
    }
    if (!this.canSaveResponses) {
      return false
    }

    await this.questionStore
      .makeCorrections(this.job.id, this.questions.answers, this.reason)
      .then(() => this.jobStore.fetchJob(this.job.id))

    this.newAlert()
      .saying('Success!', 'Your changes have been saved.')
      .withState(AlertState.Success)
      .autoDismiss()
      .show()
    this.close()
    return true
  })
}
